import { useState } from "react";
import { Platform, Pressable, ScrollView, View } from "react-native";
import Constants from "expo-constants";
import { router } from "expo-router";
import {
  TICKET_LIMITS,
  TICKET_STATUS_LABELS,
  TICKET_TYPE_LABELS,
  TICKET_TYPE_OPTIONS,
  type TicketType,
} from "@brad-tipper/app-frontend/tickets";

import { Button } from "~/components/ui/Button";
import { ModalHeader } from "~/components/ui/ModalHeader";
import { RuleLine } from "~/components/ui/RuleLine";
import { Screen } from "~/components/ui/Screen";
import { SegmentedControl } from "~/components/ui/SegmentedControl";
import { BodyText, CaptionText, Heading3 } from "~/components/ui/Text";
import { TextField } from "~/components/ui/TextField";
import { useAuth } from "~/hooks/useAuth";
import { useCreateTicket, useTickets } from "~/hooks/useTickets";
import { spacing, useTheme } from "~/styles/theme";

export default function SupportScreen() {
  const { colors } = useTheme();
  const { user } = useAuth();
  const [filter, setFilter] = useState<"open" | "closed">("open");
  const [composing, setComposing] = useState(false);
  const [type, setType] = useState<TicketType>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const tickets = useTickets(filter, { enabled: Boolean(user) });
  const create = useCreateTicket();

  const valid = title.trim().length >= 3 && description.trim().length >= 10;

  async function submit() {
    if (!valid) return;
    const ticket = await create.mutateAsync({
      type,
      title: title.trim(),
      description: description.trim(),
      appVersion: Constants.expoConfig?.version,
      platform: Platform.OS,
    });
    setTitle("");
    setDescription("");
    setComposing(false);
    router.push(`/support/${ticket.id}` as never);
  }

  return (
    <Screen>
      <ModalHeader title="Support" onClose={() => router.back()} />
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ padding: spacing.lg, gap: spacing.lg }}
      >
        <CaptionText color={colors.muted}>
          Report a bug, ask for a feature or leave feedback. Follow it here and
          add notes any time.
        </CaptionText>
        {!user ? (
          <BodyText>
            Sign in to send us a ticket and follow the replies.
          </BodyText>
        ) : (
          <>
            {composing ? (
              <View style={{ gap: spacing.md }}>
                <Heading3>New ticket</Heading3>
                <SegmentedControl
                  options={TICKET_TYPE_OPTIONS}
                  value={type}
                  onChange={setType}
                />
                <TextField
                  label="Title"
                  value={title}
                  onChangeText={setTitle}
                  maxLength={TICKET_LIMITS.title}
                  placeholder="A short summary"
                />
                <TextField
                  label="Details"
                  value={description}
                  onChangeText={setDescription}
                  maxLength={TICKET_LIMITS.description}
                  multiline
                  numberOfLines={6}
                  style={{
                    height: 140,
                    paddingTop: spacing.sm,
                    textAlignVertical: "top",
                  }}
                  placeholder={
                    type === "bug"
                      ? "What happened, and what did you expect?"
                      : "Tell us more"
                  }
                />
                {create.error ? (
                  <CaptionText color={colors.danger}>
                    {create.error instanceof Error
                      ? create.error.message
                      : "Unable to send."}
                  </CaptionText>
                ) : null}
                <Button
                  label={create.isPending ? "Sending…" : "Send"}
                  disabled={!valid || create.isPending}
                  fullWidth
                  onPress={() => void submit()}
                />
                <Button
                  label="Cancel"
                  variant="ghost"
                  fullWidth
                  onPress={() => setComposing(false)}
                />
              </View>
            ) : (
              <Button
                label="New ticket"
                fullWidth
                onPress={() => setComposing(true)}
              />
            )}
            <RuleLine />
            <SegmentedControl
              options={[
                { value: "open" as const, label: "Open" },
                { value: "closed" as const, label: "Closed" },
              ]}
              value={filter}
              onChange={setFilter}
            />
            {tickets.isLoading ? (
              <CaptionText color={colors.muted}>Loading…</CaptionText>
            ) : tickets.error ? (
              <CaptionText color={colors.danger}>
                Unable to load your tickets.
              </CaptionText>
            ) : tickets.data && tickets.data.length > 0 ? (
              tickets.data.map((ticket) => (
                <Pressable
                  key={ticket.id}
                  accessibilityRole="button"
                  accessibilityLabel={`Open ticket ${ticket.number}`}
                  onPress={() => router.push(`/support/${ticket.id}` as never)}
                  style={{
                    gap: spacing.xxs,
                    paddingVertical: spacing.md,
                    minHeight: 64,
                    justifyContent: "center",
                    borderBottomWidth: 1,
                    borderBottomColor: colors.rule,
                  }}
                >
                  <Heading3>{`#${ticket.number} ${ticket.title}`}</Heading3>
                  <CaptionText color={colors.muted}>
                    {TICKET_STATUS_LABELS[ticket.status]} ·{" "}
                    {TICKET_TYPE_LABELS[ticket.type]} · updated{" "}
                    {new Date(ticket.updatedAt).toLocaleDateString()}
                  </CaptionText>
                </Pressable>
              ))
            ) : (
              <CaptionText color={colors.muted}>
                {filter === "open" ? "No open tickets." : "No closed tickets."}
              </CaptionText>
            )}
          </>
        )}
      </ScrollView>
    </Screen>
  );
}
